import type { ComponentProps } from 'react';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import type { AlertProps } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import i18n from '@sola/locales/index.ts';

import type { LocalAlertConfig } from '../../types/localeAlerts.ts';
import { useLocalAlerts } from './local-alerts-provider.tsx';

/**
 * Single local alert.
 */
export const LocalAlert = ({
  alert,
  onClose,
  className,
  scrollIntoView = true,
  scrollPosition = 'start',
}: {
  alert: LocalAlertConfig;
  onClose?: (id: string) => void;
  className?: string;
  scrollIntoView?: AlertProps['scrollIntoView'];
  scrollPosition?: AlertProps['scrollPosition'];
}) => {
  const { translationKey, translationData, id, ...alertProps } = alert;
  // If translationKey already specifies a namespace (contains ':'), use as-is.
  // Otherwise default to the 'my' namespace.
  const i18nKey = translationKey.includes(':')
    ? translationKey
    : `my:${translationKey}`;
  const hasTitle = i18n.exists(`${i18nKey}.title`);
  let hasMessage = i18n.exists(`${i18nKey}.message`);
  let translation = hasMessage ? (
    <IntlBlock i18nKey={`${i18nKey}.message`} values={translationData} />
  ) : null;

  if (!hasMessage && !hasTitle && i18n.exists(i18nKey)) {
    hasMessage = true;
    translation = <IntlBlock i18nKey={i18nKey} values={translationData} />;
  }

  const title = hasTitle ? (
    <IntlBlock
      i18nKey={`${i18nKey}.title`}
      values={translationData}
      tOptions={{
        interpolation: {
          escapeValue: false,
        },
      }}
    />
  ) : null;

  return (
    (hasMessage || hasTitle) && (
      <Alert
        title={title}
        message={translation}
        scrollIntoView={scrollIntoView}
        scrollPosition={scrollPosition}
        closable
        onClose={onClose ? () => onClose(id) : undefined}
        className={cn('scroll-mt-roomy-md', className)}
        {...alertProps}
      />
    )
  );
};

/**
 * Component that renders all local alerts.
 */
export const LocalAlerts: React.FC<
  Pick<ComponentProps<'div'>, 'className' | 'style'> & {
    scrollPosition?: AlertProps['scrollPosition'];
  }
> = ({ className, style, scrollPosition }) => {
  const { alerts, removeAlert } = useLocalAlerts();

  if (!alerts?.length) {
    return null;
  }

  return (
    <div className={className} style={style}>
      {alerts.map((alert) => {
        return (
          <LocalAlert
            alert={alert}
            onClose={removeAlert}
            key={alert.id}
            scrollPosition={scrollPosition}
          />
        );
      })}
    </div>
  );
};
